import Button from "antd/es/button"
import Drawer from "antd/es/drawer"
import { PanelLeft, PlusIcon } from "lucide-react"
import { useCallback, useState } from "react"

import { sendAnalyticsEvent } from "../../analytics"
import ChatSendForm from "../../components/Chatv2/ChatSendForm"
import Header from "../../components/Header"
import { useActiveUserAuthorizationFromContext } from "../../contexts/ActiveUserAuthorizationContext"
import { ChatProvider, useChat } from "../../hooks/useChat"
import { UrlPathParamChatSessionsProvider } from "../../hooks/useChat/urlSessionsProvider"
import useErrorPopup from "../../hooks/useErrorPopup"
import ChatSessionComponent from "./ChatSessionComponent"
import SessionsManager from "./SessionsManager"

const ChatComponent: React.FC = () => {
  const {
    createNewQuestionMessage,
    messagesLoading,
    selectNewSession,
    messages,
  } = useChat()
  const { handleError } = useErrorPopup()
  const [chatHistoryOpen, setChatHistoryOpen] = useState<boolean>(false)
  const { authUser } = useActiveUserAuthorizationFromContext()

  const openHistorySidebar = useCallback(() => setChatHistoryOpen(true), [])
  const closeHistorySidebar = useCallback(() => setChatHistoryOpen(false), [])
  const createNewSession = useCallback(() => {
    selectNewSession()
    sendAnalyticsEvent({
      surface: "WEB_CHAT",
      event_type: "CLICK",
      event_data: {
        entity_id: "WEB_CHAT_HEADER_NEW_SESSION",
      },
    })
    closeHistorySidebar()
  }, [selectNewSession, closeHistorySidebar])

  const sendChatMessage = useCallback(
    async (questionText: string) => {
      try {
        await createNewQuestionMessage(questionText)
      } catch (error) {
        handleError({ error, prefix: "Unable to send message" })
      }
    },
    [createNewQuestionMessage, handleError],
  )

  const onSubmitCTAQuestion = useCallback(
    async (question: string) => {
      // TODO(mgraczyk): Add sessionId here.
      await sendChatMessage(question)
    },
    [sendChatMessage],
  )

  const hasTooLongError =
    messages[messages.length - 1]?.error?.error === "CONVERSATION_TOO_LONG"

  return (
    <>
      <Header
        title="Knowledge Assistant"
        subtitle="Chat with your Knowledge Assistant"
      >
        <Button icon={<PlusIcon />} onClick={createNewSession} className="mr-2">
          New Thread
        </Button>
        <Button icon={<PanelLeft />} onClick={openHistorySidebar}>
          History
        </Button>
      </Header>

      <div className="mx-auto flex w-full max-w-[1600px] grow flex-col overflow-y-hidden">
        <ChatSessionComponent
          user={authUser}
          onSubmitCTAQuestion={onSubmitCTAQuestion}
        />
        <ChatSendForm
          id="chat-container"
          className="p-2"
          sendChatMessage={sendChatMessage}
          loading={messagesLoading}
          disabled={hasTooLongError}
        />
      </div>
      <Drawer
        open={chatHistoryOpen}
        onClose={closeHistorySidebar}
        title="Chat History"
      >
        <SessionsManager onSessionChange={closeHistorySidebar} />
      </Drawer>
    </>
  )
}

const ChatPage = () => {
  return (
    <UrlPathParamChatSessionsProvider sourceKind="WEBAPP">
      <ChatProvider>
        <ChatComponent />
      </ChatProvider>
    </UrlPathParamChatSessionsProvider>
  )
}

export default ChatPage
